import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {computeIsAxisGhostCell} from 'notebook/tables/store/logic/TableDimensionsLogic';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createDebuggableSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

/**
 * The model for a single axis (header) cell. `TableAxisCell` renders both the row header (from
 * `TableRow`) and the column header (from `TableHeaderRow`), so an axis cell is keyed by its axis
 * and its index along that axis rather than by row x column like {@link CellModel}.
 */
export interface AxisCellModel {
  /** The axis this header cell labels. */
  dim: TableDimension;
  /** The row index when `dim` is the row axis, otherwise the column index. */
  index: number;
  /** Whether this is a placeholder cell rendered outside of the table's data bounds. */
  isGhostCell: boolean;
}

/**
 * Creates a selector for the axis cell at (`dim`, `index`) against the table store. Used for axis
 * cells rendered inside a table; standalone axis cells use {@link makeStandaloneAxisCellModel}.
 */
export function createAxisCellModelSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  dim: TableDimension,
  index: number
): (store: TableEditableStore) => AxisCellModel {
  return createDebuggableSelector.withTypes<TableEditableStore>()(
    [(store) => tableSelector(store).props.table.table[0].length],
    (columnCount): AxisCellModel => ({
      dim,
      index,
      isGhostCell: computeIsAxisGhostCell(dim, index, columnCount),
    })
  );
}

/**
 * An {@link AxisCellModel} for an axis cell rendered outside of any table store (for example
 * `SimpleTableAxisCell`). Such cells only ever label real dimensions, so they are never ghost cells.
 */
export function makeStandaloneAxisCellModel(dim: TableDimension, index: number): AxisCellModel {
  return {dim, index, isGhostCell: false};
}
